import useMCPContext from "@/pages/mcp/useMCPContext";
import { Button } from "@altimateai/lego";
import useIntegrations from "../useIntegrations";
import { CommonIntegrationInstance, Teammate } from "@/pages/mcp/mcpSlice";
import { useAppState } from "@/modules/app/useAppContext";
import { getTenant } from "@/helpers/tenant";
import { API_URL } from "@/config";

interface ToolConfig {
  key?: string;
  value?: unknown;
}

interface DownloadConfigurationsTemplateProps {
  teammate?: Teammate;
}

const formatKeyToReadable = (key: string): string => {
  // Split on uppercase letters and join with space
  const words = key.split(/(?=[A-Z])/);
  // Capitalize first letter of each word
  return words.map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(" ");
};

const DownloadConfigurationsTemplate = ({
  teammate: teammateProp,
}: DownloadConfigurationsTemplateProps) => {
  const { teammate: teammateFromContext } = useMCPContext();
  // Use prop if provided, otherwise fall back to context
  const teammate = teammateProp ?? teammateFromContext;
  const { allIntegrations, isIntegrationsFromServerLoading } = useIntegrations();
  const { loggedinUser: user } = useAppState();
  const tenant = getTenant();

  const handleDownload = () => {
    if (!teammate?.id || !teammate?.integrations || !allIntegrations) return;

    const connections = teammate.integrations
      .map(integration => {
        const serverIntegration = allIntegrations.find(i => i.id === integration.id) as
          | CommonIntegrationInstance
          | undefined;
        console.log(serverIntegration);
        if (!serverIntegration?.config?.length) return null;

        return {
          name: `${serverIntegration.id}-local`,
          integration_id: serverIntegration.id,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
          config:
            serverIntegration.config?.reduce(
              (acc: Record<string, unknown>, tool: ToolConfig) => {
                if (tool.key) {
                  acc[tool.key] = `--Your ${formatKeyToReadable(tool.key)} here--`;
                }
                return acc;
              },
              {} as Record<string, unknown>
            ) || {},
          id: crypto.randomUUID(),
        };
      })
      .filter((c): c is NonNullable<typeof c> => c !== null);

    const settings = {
      [teammate.id]: connections.reduce(
        (acc: Record<string, unknown>, connection) => {
          if (connection) {
            acc[connection.integration_id] = connection.id;
          }
          return acc;
        },
        {} as Record<string, unknown>
      ),
    };

    const blob = new Blob(
      [
        JSON.stringify(
          {
            connections,
            settings,
            datamate_id: teammate.id,
            altimateUrl: API_URL,
            altimateInstanceName: tenant,
            altimateApiKey: user?.auth_token,
          },
          null,
          2
        ),
      ],
      {
        type: "application/json",
      }
    );
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${teammate.name}-configurations-template.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  return (
    <div>
      <Button
        disabled={isIntegrationsFromServerLoading || !allIntegrations.length}
        onClick={handleDownload}
      >
        Download Configurations Template
      </Button>
    </div>
  );
};

export default DownloadConfigurationsTemplate;
